import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useEstateAppApiContext } from "src/contexts/snug/estateAppApi-context";
import { paths } from "src/paths";
import {
  goToNetworkLanding,
  loadNetworkAccounts,
  NetworkAccountsResponse,
  NetworkSwitchResponse,
  restoreNetworkHome,
  startNetworkSession,
} from "src/utils/snug/network-session";

export const NETWORK_ACCOUNTS_PATH = "/v3/network/accounts/";
export const NETWORK_SWITCH_PATH = "/v3/network/switch/";

interface UseNetworkAccounts {
  network: NetworkAccountsResponse | null;
  loading: boolean;
  error: boolean;
  switchingRoleId: string | null;
  switchTo: (roleId: string) => Promise<void>;
  backToNetwork: () => Promise<void>;
}

// The network accounts this login can switch into. Empty for everyone outside a network.
export const useNetworkAccounts = (): UseNetworkAccounts => {
  const { estateAppApiInstance } = useEstateAppApiContext();
  const [network, setNetwork] = useState<NetworkAccountsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [switchingRoleId, setSwitchingRoleId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    loadNetworkAccounts(async () => {
      const res = await estateAppApiInstance.get(NETWORK_ACCOUNTS_PATH);
      return res.data?.data;
    })
      .then((data) => {
        if (active) setNetwork(data);
      })
      .catch((err) => {
        console.error("[useNetworkAccounts] failed to load network accounts", err);
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [estateAppApiInstance]);

  const switchTo = useCallback(
    async (roleId: string): Promise<void> => {
      setSwitchingRoleId(roleId);
      try {
        const res = await estateAppApiInstance.post(NETWORK_SWITCH_PATH, {
          role_id: roleId,
        });
        const data: NetworkSwitchResponse = res.data?.data;
        if (!data?.access) {
          throw new Error("No access token in switch response");
        }
        startNetworkSession(data.access);
        // Full navigation so every context reloads as the owner account
        window.location.replace(paths.dashboard.index);
      } catch (err: any) {
        setSwitchingRoleId(null);
        // mfa_required is routed to MFA setup / sign-in by the estate API interceptor
        if (err?.response?.data?.code === "mfa_required") return;
        if (err?.response?.status === 404) {
          toast.error("You don't have access to that pro group.");
        } else {
          toast.error("We couldn't switch pro groups. Please try again.");
        }
        console.error("[useNetworkAccounts] switch failed", err);
      }
    },
    [estateAppApiInstance]
  );

  const backToNetwork = useCallback(async (): Promise<void> => {
    await restoreNetworkHome();
    goToNetworkLanding();
  }, []);

  return { network, loading, error, switchingRoleId, switchTo, backToNetwork };
};
